import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  KycTraceEvent,
  KycTraceLevel,
  KycTracePhase,
  KycTraceService,
} from 'src/app/core/services/kyc-trace.service';
import { KycTraceReadService, ServerAudit } from 'src/app/core/services/kyc-trace-read.service';

interface PhaseMeta {
  icon: string;
  label: string;
}

type Tone = 'ok' | 'bad' | 'warn' | 'pre' | 'neutral';

/**
 * KYC Attempt Timeline — a single, reusable card that renders the full timestamped trace of a
 * KYC attempt (capture → classification → OCR → QR → pre-review → submit → result) as an
 * inspectable vertical timeline.
 *
 * Two data sources, picked by input:
 *  - `live`      — subscribe to the in-memory {@link KycTraceService} (the client's own attempt,
 *                  used by the /kyc-test harness as the modal runs).
 *  - `kycCheckId`/`attemptId` — load the RECORDED trace back from `cp_event` via
 *                  {@link KycTraceReadService} (the firm/agent side, and any past attempt).
 *
 * Copy is plain English: an internal diagnostic surface for agents/developers, not a client screen.
 */
@Component({
  selector: 'app-kyc-attempt-timeline',
  templateUrl: './kyc-attempt-timeline.component.html',
  styleUrls: ['./kyc-attempt-timeline.component.scss'],
})
export class KycAttemptTimelineComponent implements OnInit, OnChanges, OnDestroy {
  /** Recorded source: the `cp_kyc_check.id` whose trace to load. */
  @Input() kycCheckId: string | null = null;
  /** Recorded source (alternative): a specific attempt id. */
  @Input() attemptId: string | null = null;
  /** Live source: mirror the in-memory trace of the attempt in progress. */
  @Input() live = false;
  /** Hide the header (icon + title + blurb) when embedding under an existing heading. */
  @Input() showHeader = true;

  events: KycTraceEvent[] = [];
  attemptLabel: string | null = null;
  /** Server-stamped audit (source IP + receive time), recorded source only. */
  serverAudit: ServerAudit | null = null;
  loading = false;
  error: string | null = null;

  /** Which events have their detail expanded. */
  private readonly expanded = new Set<number>();
  private liveSub?: Subscription;
  private initialized = false;

  readonly phaseMeta: Record<KycTracePhase, PhaseMeta> = {
    system: { icon: 'flag', label: 'System' },
    capture: { icon: 'photo_camera', label: 'Capture' },
    classify: { icon: 'category', label: 'Classify' },
    ocr: { icon: 'document_scanner', label: 'OCR' },
    qr: { icon: 'qr_code_2', label: 'QR / MRZ' },
    precheck: { icon: 'fact_check', label: 'Pre-check' },
    submit: { icon: 'cloud_upload', label: 'Submit' },
    poll: { icon: 'sync', label: 'Poll' },
    result: { icon: 'verified', label: 'Result' },
  };

  constructor(
    private trace: KycTraceService,
    private reader: KycTraceReadService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.wire();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) return;
    if (changes['live'] || changes['kycCheckId'] || changes['attemptId']) this.wire();
  }

  ngOnDestroy(): void {
    this.liveSub?.unsubscribe();
  }

  /** (Re)connect to whichever source the current inputs select. */
  private wire(): void {
    this.liveSub?.unsubscribe();
    this.liveSub = undefined;
    this.expanded.clear();

    this.serverAudit = null;
    if (this.live) {
      this.attemptLabel = this.trace.attemptLabel;
      this.liveSub = this.trace.events$.subscribe((e) => {
        this.events = e;
        this.attemptLabel = this.trace.attemptLabel;
      });
      return;
    }
    void this.load();
  }

  /** Load the recorded trace for the given check / attempt. */
  async load(): Promise<void> {
    if (!this.kycCheckId && !this.attemptId) {
      this.events = [];
      this.attemptLabel = null;
      return;
    }
    this.loading = true;
    this.error = null;
    try {
      const rec = this.kycCheckId
        ? await this.reader.forCheck(this.kycCheckId)
        : await this.reader.forAttempt(this.attemptId!);
      this.events = rec.events;
      this.attemptLabel = rec.attemptLabel;
      this.serverAudit = rec.serverAudit;
    } catch (e: unknown) {
      this.error = e instanceof Error ? e.message : 'Failed to load the recorded timeline.';
      this.events = [];
    } finally {
      this.loading = false;
    }
  }

  /** Reload (recorded) or clear (live) — surfaced as the header action. */
  refresh(): void {
    if (this.live) this.clear();
    else void this.load();
  }

  get canRefresh(): boolean {
    return this.live || !!this.kycCheckId || !!this.attemptId;
  }

  meta(phase: KycTracePhase): PhaseMeta {
    return this.phaseMeta[phase] ?? this.phaseMeta.system;
  }

  tone(level: KycTraceLevel): Tone {
    return level === 'success' ? 'ok' : level === 'error' ? 'bad' : level === 'warn' ? 'warn' : 'neutral';
  }

  durationLabel(events: KycTraceEvent[]): string {
    if (events.length < 2) return '0ms';
    return this.offset(events[events.length - 1].offsetMs);
  }

  offset(ms: number): string {
    return ms < 1000 ? `+${ms}ms` : `+${(ms / 1000).toFixed(2)}s`;
  }

  isExpanded(seq: number): boolean {
    return this.expanded.has(seq);
  }

  toggle(e: KycTraceEvent): void {
    if (!this.hasDetail(e)) return;
    if (this.expanded.has(e.seq)) this.expanded.delete(e.seq);
    else this.expanded.add(e.seq);
  }

  hasDetail(e: KycTraceEvent): boolean {
    return !!e.detail && Object.keys(e.detail).length > 0;
  }

  detailEntries(e: KycTraceEvent): Array<{ k: string; v: string }> {
    if (!e.detail) return [];
    return Object.entries(e.detail).map(([k, v]) => ({ k, v: this.fmt(v) }));
  }

  copyJson(events: KycTraceEvent[]): void {
    const text = JSON.stringify(events, null, 2);
    navigator.clipboard?.writeText(text).catch(() => {
      /* best-effort */
    });
  }

  /** Only meaningful for the live source (drops the in-memory trace). */
  clear(): void {
    this.trace.clear();
    this.expanded.clear();
  }

  trackBySeq(_i: number, e: KycTraceEvent): number {
    return e.seq;
  }

  private fmt(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'object') {
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    }
    return String(v);
  }
}
